import React, { useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { ResourceName } from "./CatalogAdmin.types.tsx";
import { importMessages } from "./model.tsx";
import { resourceSpec } from "./resources.tsx";

import { importCsv } from "../../services/catalog.services.tsx";

interface ImportCsvModalProps {
  resource: ResourceName;
  onHide: () => void;
  onImported: (created: number) => void;
}

const ImportCsvModal: React.FC<ImportCsvModalProps> = ({ resource, onHide, onImported }) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [messages, setMessages] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const download = () => {
    const header = resourceSpec(resource).csvHeader.join(",");
    const url = URL.createObjectURL(new Blob([`${header}\n`], { type: "text/csv" }));
    const link = document.createElement("a");

    link.href = url;
    link.download = `${resource}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!file) {
      return;
    }

    setBusy(true);
    setMessages([]);

    try {
      onImported(await importCsv(resource, file));
    } catch (error) {
      setMessages(importMessages(error, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show onHide={onHide} centered size="lg" className="catalog-modal">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {t("catalog.import.title", { resource: t(`catalog.resources.${resource}`) })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("catalog.import.note")}</p>
          {resource === "chillers" && <p>{t("catalog.import.chillersNote")}</p>}
          <Button variant="outline-secondary" size="sm" className="mb-3" onClick={download}>
            {t("catalog.import.downloadHeader")}
          </Button>
          <Form.Group controlId="catalog-import-file">
            <Form.Label>{t("catalog.import.file")}</Form.Label>
            <Form.Control
              type="file"
              accept=".csv"
              onChange={(event) => setFile((event.target as HTMLInputElement).files?.[0] ?? null)}
            />
          </Form.Group>
          {messages.length > 0 && (
            <Alert variant="danger" className="mt-3">
              <ul className="mb-0">
                {messages.map((message, index) => (
                  <li key={`${index}-${message}`}>{message}</li>
                ))}
              </ul>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            {t("catalog.form.cancel")}
          </Button>
          <Button type="submit" variant="success" disabled={busy || !file}>
            {t("catalog.import.submit")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ImportCsvModal;
